import {
  Flex,
  Icon,
  Skeleton,
  useDisclosure,
  VStack,
  Box,
  HStack,
} from "@chakra-ui/react";
import { isEmpty, debounce, isNull } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import urls from "../../../urls";
import {
  clearCampaignDetails,
  getCampaign,
  selectCampaign,
  publishCampaign,
  saveCampaignDraft,
  setActiveErrorCheck,
  updateCampaign,
  setPublishLoadState,
  publishCampaignDraft,
  setCampaignDetails,
  setCampaignTags,
  DL_LIST_ALL_ACTION,
  TEMPLATE_Of_CAMPAIGN_ACTION,
} from "./campaignSlice";
import CampaignSmartflow from "./flow/CampaignSmartflow";
import { toast } from "react-toastify";
import CampaignModal from "./components/CampaignModal";
import { CAMPAIGN_TYPE } from "../../../common/constants/campaign";
import {
  CampaignRuleResp,
  CampaignSchedule,
  CAMPAIGN_CONTEXT,
  DraftPublishType,
  DraftSendDataType,
  DynamicListType,
  FullCampaign,
} from "../../../common/types/campaign";
import { selectAccount } from "../../account/accountSlice";
import { formatDistanceToNow, subHours } from "date-fns";
import {
  validateDynamicList,
  validateSchedule,
} from "./helper/validationHelper";
import {
  formatRule,
  formatSchedule,
  formatScheduleData,
  removeConnector,
} from "./helper/formatHelper";
import IButton from "../../../components/IButton";
import {
  formatLastSaved,
  isFailed,
  isFulfilled,
  isLoading,
  isSuccess,
} from "../../../common/helper/commonHelper";
import { useAppDispatch } from "../../../store";
import EditNameModal, {
  ASSET_NAME_ACTIONS,
} from "../../../components/EditNameModal";
import EditableHeading from "../../../components/EditableHeading";
import {
  checkCampaignHistory,
  resetReports,
  selectCampaignReports,
} from "./campaignreports/campaignReportsSlice";
import SpinnerContainer from "../../../components/SpinnerContainer";
import { FlowGraph, FlowValidity } from "../../../common/types/flow";
import CampaignSidebar from "./sidebar/CampaignSidebar";
import { LoadingWithData } from "../../../common/types/common";
import CampaignScheduleSection from "./sidebar/sections/CampaignScheduleSection";
import CampaignExitSection from "./sidebar/sections/CampaignExitSection";
import AudienceFilterSection from "./sidebar/sections/AudienceFilterSection";
import LinkedTags from "../tag/LinkedTags";
import { ASSET_TYPES } from "../../../common/constants/common";
import IconWithTooltip from "../../../components/IconWithTooltip";
import { IconType } from "react-icons";
import TopBar from "./components/TopBar";
import CommonDrawer from "./components/CommonDrawer";
import CampaignReports from "./campaignreports/CampaignReports";
import { expireApiStatus } from "../../../common/slices/apiStatusSlice";
import { publishDraftApi } from "../../../common/api/campaign/campaign";

const TIME_UNIT = 20000;

function CampaignTitle({
  campaignDetails,
  setIsOpenRenameModal,
  campaignTypeSpecifics,
  campaignTags,
}: {
  campaignTags: string[];
  campaignDetails: LoadingWithData<FullCampaign>;
  setIsOpenRenameModal: (val: string) => void;
  campaignTypeSpecifics: { label: string; color: string; icon: IconType };
}) {
  const dispatch = useDispatch();
  return (
    <Box w="100%">
      {isLoading(campaignDetails.loading) ? (
        <Skeleton height="24px" width="100px" />
      ) : (
        <Box w="100%">
          <HStack alignItems="center" w="100%">
            <IconWithTooltip
              icon={campaignTypeSpecifics.icon}
              color={campaignTypeSpecifics.color}
              fontSize="18px"
              label={campaignTypeSpecifics.label}
            />
            <EditableHeading
              name={campaignDetails.data.name}
              openModal={() => setIsOpenRenameModal(campaignDetails.data.name)}
            />
          </HStack>
          <LinkedTags
            tags={campaignTags}
            assetId={campaignDetails.data.campaign_id}
            assetType={ASSET_TYPES.CAMPAIGN}
            onTagsChange={(ids: string[]) => dispatch(setCampaignTags(ids))}
          />
        </Box>
      )}
    </Box>
  );
}

export default function CampaignBuilder() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isCampaignEditable, setIsCampaignEditable] = useState(false);
  const [lastSaved, setLastSaved] = useState("");

  let { id } = useParams<{ id: string }>();

  const {
    campaignDetails,
    publishingCampaign,
    dynamicList: { operatorsArgCount },
    campaignsChanged,
    savingDraft,
    campaignTags,
  } = useSelector(selectCampaign);

  const { user } = useSelector(selectAccount);

  const { showCampaignReportsTabLoader } = useSelector(selectCampaignReports);

  const [dynamicList, setDynamicList] = useState<DynamicListType[]>([]);
  const [flow, setFlow] = useState<FlowGraph>({ nodes: [], links: [] });

  const [campaignSchedule, setCampaignSchedule] = useState<CampaignSchedule>(
    formatScheduleData(
      campaignDetails.data.draft_data?.schedule ??
        campaignDetails.data.schedule,
      campaignDetails.data.campaign_id
    )
  );

  const [campaignRule, setCampaignRule] = useState<CampaignRuleResp>(
    campaignDetails.data.draft_data.rule ?? campaignDetails.data.rule
  );
  const [flowValidity, setFlowValidity] = useState<FlowValidity>({});
  const [ruleValid, setRuleValid] = useState(true);
  const [validity, setValidity] = useState({
    dynamicList: "",
    flow: "",
    schedule: "",
    rule: "",
    exitCriteria: "",
  });

  useEffect(() => {
    const formattedSchedule = formatScheduleData(
      campaignDetails.data.draft_data?.schedule ??
        campaignDetails.data.schedule,
      campaignDetails.data.campaign_id
    );
    setCampaignSchedule(formattedSchedule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignDetails.data.campaign_id]);

  // Support for old urls | Replacing old urls to new
  useEffect(() => {
    navigate(`${urls.journey}/${id}`, { replace: true });
  }, [navigate, id]);

  useEffect(() => {
    setCampaignRule(
      campaignDetails.data.draft_data.rule ?? campaignDetails.data.rule
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignDetails.data.campaign_id]);

  const updateLastSavedStatus = useCallback((data: FullCampaign) => {
    if (data) {
      const updatedAt = data.draft_data?.updated_at ?? data.updated_at;
      setLastSaved(formatLastSaved({ updatedAt, includeSeconds: true }));
    }
  }, []);

  useEffect(() => {
    updateLastSavedStatus(campaignDetails.data);
    const intervalId = setInterval(
      () => updateLastSavedStatus(campaignDetails.data),
      TIME_UNIT
    );
    return () => clearInterval(intervalId);
  }, [campaignDetails.data, updateLastSavedStatus]);

  const [isOpenRenameModal, setIsOpenRenameModal] = useState<string | null>(
    null
  );

  function renameCampaignHandler(updatedName: string) {
    setIsOpenRenameModal(null);
    if (campaignDetails.data && campaignDetails.data.campaign_id) {
      dispatch(
        updateCampaign({
          id: campaignDetails.data.campaign_id,
          name: updatedName,
        })
      );
    }
  }

  useEffect(() => {
    return () => {
      dispatch(clearCampaignDetails());
      dispatch(resetReports());
      dispatch(
        expireApiStatus([
          { actionName: DL_LIST_ALL_ACTION },
          { actionName: TEMPLATE_Of_CAMPAIGN_ACTION, expireAll: true },
        ])
      );
    };
  }, [dispatch]);

  function goToCampaignList() {
    navigate(urls.journey);
  }

  useEffect(() => {
    if (isFailed(campaignDetails.loading)) {
      navigate(urls.journey);
    }
  }, [navigate, campaignDetails.loading]);

  useEffect(() => {
    if (id) {
      dispatch(getCampaign(id));
      dispatch(checkCampaignHistory(id));
    }
  }, [id, dispatch, campaignsChanged]);

  useEffect(() => {
    if (!campaignDetails.data) {
      toast.error("Journey details not available");
      navigate(urls.journey, { replace: true });
    } else {
      setIsCampaignEditable(!campaignDetails.data.active);
    }
  }, [campaignDetails.data, navigate]);

  const {
    isOpen: isEditWarningOpen,
    onOpen: onEditWarningOpen,
    onClose: onEditWarningClose,
  } = useDisclosure();
  const {
    isOpen: isOverrideWarningOpen,
    onOpen: onOverrideWarningOpen,
    onClose: onOverrideWarningClose,
  } = useDisclosure();

  useEffect(() => {
    const dynamicList =
      campaignDetails.data.draft_data?.dynamic_list ??
      campaignDetails.data.dynamic_list;
    setDynamicList(dynamicList?.query.conditions || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignDetails.data.campaign_id]);

  useEffect(() => {
    if (
      campaignDetails.data?.draft_data?.updated_by?.id &&
      user.id &&
      new Date(campaignDetails.data.draft_data?.updated_at ?? "") >
        subHours(new Date(), 1) &&
      campaignDetails.data.draft_data?.updated_by.id !== user.id
    ) {
      onOverrideWarningOpen();
    } else {
      onOverrideWarningClose();
    }
  }, [
    campaignDetails.data,
    onOverrideWarningOpen,
    onOverrideWarningClose,
    user.id,
  ]);

  const saveDraft = useCallback(
    (current: DraftSendDataType) => {
      if (!campaignDetails.data.active && !publishingCampaign) {
        dispatch(saveCampaignDraft(current));
      }
    },
    [campaignDetails.data.active, publishingCampaign, dispatch]
  );

  function setNullIfEmpty(data: any) {
    return isEmpty(data) ? null : data;
  }

  function withOtherTabData(newData: DraftSendDataType): DraftSendDataType {
    return {
      flow: flow,
      query: { conditions: isEmpty(dynamicList) ? [] : dynamicList },
      schedule: setNullIfEmpty(formatSchedule(campaignSchedule)),
      rule: setNullIfEmpty(formatRule(campaignRule)),
      exit_criteria: exitCriteria ? { conditions: exitCriteria } : null,
      ...newData,
    };
  }

  const debouncedSave = useMemo(() => {
    return debounce(saveDraft, 2000);
  }, [saveDraft]);

  function validateData(callback: (param?: any) => void, ...args: any[]) {
    dispatch(setActiveErrorCheck(true));
    const { dynamicListValid, dynamicListValidated } = validateDynamicList(
      dynamicList,
      operatorsArgCount
    );
    setDynamicList(dynamicListValidated);
    const flowValid =
      Object.values(flowValidity).every((valid) => valid) &&
      !isEmpty(flow.nodes);
    const scheduleErrors = validateSchedule(campaignSchedule);
    const scheduleValid = !Object.values(scheduleErrors).length;

    let exitCriteriaValid = true;
    if (!isNull(exitCriteria)) {
      const { dynamicListValid, dynamicListValidated: exitCriteriaValidated } =
        validateDynamicList(exitCriteria ?? [], operatorsArgCount);

      exitCriteriaValid = dynamicListValid;
      setExitCriteria([exitCriteriaValidated[0]]);
    }

    setValidity({
      dynamicList: dynamicListValid
        ? ""
        : isEmpty(dynamicList)
        ? "Dynamic List is empty"
        : "Some fields are invalid",
      flow: flowValid
        ? ""
        : isEmpty(flow)
        ? "Flow is empty"
        : "Some fields are invalid",
      schedule: scheduleValid ? "" : "Some fields are invalid",
      rule: ruleValid ? "" : "Journey rule is invalid",
      exitCriteria: exitCriteriaValid ? "" : "Some fields are invalid",
    });
    if (
      dynamicListValid &&
      flowValid &&
      ruleValid &&
      scheduleValid &&
      exitCriteriaValid
    ) {
      callback.apply(null, [args[0]]);
    } else {
      toast.error("Some fields are invalid");
    }
  }

  const publishIfValid = useCallback(
    (details: DraftPublishType, active: boolean) => {
      if (!details.error) {
        dispatch(publishCampaign({ active }));
      } else {
        const errors = details.error_details;
        const { dynamic_list, flow, schedule, rule, exit_criteria } = errors;
        if (!isEmpty(errors)) {
          setValidity({
            dynamicList: dynamic_list,
            flow,
            schedule,
            rule,
            exitCriteria: exit_criteria,
          });
          const errorMsg = Object.entries(errors)
            .map(([key, value]) => `${key}: ${value}`)
            .join(", ");
          toast.error(errorMsg, { autoClose: false });
        }
      }
    },
    [dispatch]
  );

  async function publishData(active: boolean) {
    dispatch(setPublishLoadState(true));
    const data = {
      query: { conditions: removeConnector(dynamicList) },
      schedule: formatSchedule(campaignSchedule),
      rule: formatRule(campaignRule),
      flow: flow,
      exit_criteria: exitCriteria ? { conditions: exitCriteria } : null,
    };
    try {
      const saveResult = await dispatch(saveCampaignDraft(data));
      if (isFulfilled(saveResult.meta.requestStatus)) {
        const publishDraftResult = await dispatch(publishCampaignDraft());
        if (isFulfilled(publishDraftResult.meta.requestStatus)) {
          const newCampaignDetails = publishDraftResult.payload as Awaited<
            ReturnType<typeof publishDraftApi>
          >;
          dispatch(setCampaignDetails(newCampaignDetails.campaign));
          publishIfValid(newCampaignDetails, active);
        }
      }
      dispatch(setPublishLoadState(false));
    } catch (errors) {
      toast.error("Journey publish failed");
      dispatch(setPublishLoadState(false));
    }
  }

  function onPublish(active: boolean) {
    validateData(publishData, active);
  }

  const {
    isOpen: isOpenScheduleForm,
    onOpen: onOpenScheduleForm,
    onClose: onCloseScheduleForm,
  } = useDisclosure();

  const {
    isOpen: isOpenReports,
    onOpen: onOpenReports,
    onClose: onCloseReports,
  } = useDisclosure();

  const { showCampaignReportsTab } = useSelector(selectCampaignReports);

  const {
    isOpen: isOpenAudienceList,
    onOpen: onOpenAudienceList,
    onClose: onCloseAudienceList,
  } = useDisclosure();

  const campaignTypeSpecifics: (typeof CAMPAIGN_TYPE)[CAMPAIGN_CONTEXT] =
    useMemo(() => {
      return CAMPAIGN_TYPE[
        campaignDetails?.data?.campaign_context ?? CAMPAIGN_CONTEXT.PERSON
      ];
    }, [campaignDetails]);

  const [exitCriteria, setExitCriteria] = useState<[DynamicListType] | null>(
    null
  );

  useEffect(() => {
    const exitCriteria =
      campaignDetails.data.draft_data?.exit_criteria?.conditions ??
      campaignDetails.data.exit_criteria?.conditions;
    setExitCriteria(exitCriteria || null);
  }, [
    campaignDetails.data.campaign_id,
    campaignDetails.data.draft_data?.exit_criteria,
    campaignDetails.data.exit_criteria,
  ]);

  function onExitCriteriaChange(exitCriteria: DynamicListType | null) {
    setExitCriteria(exitCriteria ? [exitCriteria] : null);
    const data = withOtherTabData({
      exit_criteria: exitCriteria ? { conditions: [exitCriteria] } : null,
    });

    debouncedSave(data);
  }

  return (
    <SpinnerContainer
      loading={showCampaignReportsTabLoader || !campaignDetails.data}
      height="500px"
    >
      <Flex w="100%" h="100vh">
        <CampaignSidebar
          heading={
            <Flex alignItems="center">
              <IButton
                size="md"
                aria-label="Go back"
                variant="link"
                name="return-button"
                onClick={goToCampaignList}
                fontSize="xs"
                leftIcon={<Icon fontSize="xs" as={FaArrowLeft} />}
              >
                Back to journeys list
              </IButton>
            </Flex>
          }
          title={
            <CampaignTitle
              campaignDetails={campaignDetails}
              setIsOpenRenameModal={setIsOpenRenameModal}
              campaignTypeSpecifics={campaignTypeSpecifics}
              campaignTags={campaignTags}
            />
          }
        >
          {/* Backend isn't supported to implement this */}
          {/* <AudienceCountSummary count={123123} /> */}

          <CampaignScheduleSection
            readonly={!isCampaignEditable}
            errorText={validity.rule || validity.schedule}
            isOpen={isOpenScheduleForm}
            onClose={onCloseScheduleForm}
            onOpen={onOpenScheduleForm}
            schedule={campaignSchedule}
            onChange={(schedule, rule) => {
              setCampaignSchedule(schedule);
              setCampaignRule(rule);
              debouncedSave(
                withOtherTabData({
                  schedule: formatSchedule(schedule),
                  rule: formatRule(rule),
                })
              );
            }}
            rule={campaignRule}
            onRuleValidityChange={setRuleValid}
          />

          <CampaignExitSection
            readonly={!isCampaignEditable}
            errorText={validity.exitCriteria}
            data={exitCriteria ? exitCriteria[0] : null}
            onChange={onExitCriteriaChange}
          />

          <AudienceFilterSection
            isOpen={isOpenAudienceList}
            onClose={onCloseAudienceList}
            onOpen={onOpenAudienceList}
            data={dynamicList}
            readonly={!isCampaignEditable}
            errorText={validity.dynamicList}
            saveDraft={(data: DynamicListType[]) =>
              debouncedSave(
                withOtherTabData({
                  query: { conditions: data },
                })
              )
            }
            onChange={(data: DynamicListType[]) => {
              setDynamicList(data);
            }}
          />
        </CampaignSidebar>

        <VStack w="100%" spacing="0">
          <TopBar
            isActive={!!campaignDetails.data.active}
            updatedBy={campaignDetails.data.draft_data?.updated_by}
            isDraft={!!campaignDetails.data.draft}
            isLoadingSuccess={isSuccess(campaignDetails.loading)}
            currentUserId={user.id}
            savingDraft={savingDraft}
            lastSaved={lastSaved}
            isCampaignEditable={isCampaignEditable}
            onEditWarningOpen={onEditWarningOpen}
            publishingCampaign={publishingCampaign}
            onPublish={onPublish}
            openReports={showCampaignReportsTab ? onOpenReports : undefined}
          />
          <CampaignSmartflow
            setActions={setFlow}
            setFlowValidity={setFlowValidity}
            readonly={!isCampaignEditable}
          />
        </VStack>
      </Flex>
      <CampaignModal
        isOpenModal={isEditWarningOpen}
        onCancel={onEditWarningClose}
        submitHandler={() => {
          dispatch(
            publishCampaign({
              active: false,
            })
          );
          onEditWarningClose();
        }}
        header="Edit active journey"
        text="Editing an Active journey would pause the current run. Do you want to continue?"
        primaryButton="Edit journey"
      />

      <EditNameModal
        value={isOpenRenameModal ?? ""}
        action={ASSET_NAME_ACTIONS.RENAME}
        isOpen={isOpenRenameModal !== null}
        onClose={() => setIsOpenRenameModal(null)}
        onSubmit={renameCampaignHandler}
        asset="journey"
      />

      <CampaignModal
        isOpenModal={isOverrideWarningOpen}
        onCancel={() => {
          onOverrideWarningClose();
          goToCampaignList();
        }}
        submitHandler={() => {
          onOverrideWarningClose();
        }}
        header="Data override warning"
        text={`${
          campaignDetails.data?.draft_data?.updated_by?.name
        } updated this journey ${
          campaignDetails.data?.draft_data?.updated_at
            ? formatDistanceToNow(
                new Date(campaignDetails.data.draft_data?.updated_at),
                {
                  addSuffix: true,
                }
              )
            : ""
        }. Before continuing make sure that they are done with editing. 
          Editing a journey at the same time could lead to overwrites.`}
        primaryButton="Ok"
      />

      <CommonDrawer
        title="Journey Reports"
        isOpen={isOpenReports}
        onClose={onCloseReports}
        size="full"
      >
        {isSuccess(campaignDetails.loading) && <CampaignReports />}
      </CommonDrawer>
    </SpinnerContainer>
  );
}
